import type { Locale } from '../i18n/types';

export type LegalPageKey = 'privacy' | 'terms';

export interface DocumentSection {
    title: string;
    paragraphs: readonly string[];
    items?: readonly string[];
}

export interface DocumentPageContent {
    kind: 'document';
    eyebrow: string;
    title: string;
    description: string;
    /** ISO calendar date of the latest revision. */
    updatedAt: string;
    sections: readonly DocumentSection[];
    seoTitle: string;
    seoDescription: string;
}

const updatedAt = '2026-09-11';

export const legalPageContent: Readonly<
    Record<Locale, Readonly<Record<LegalPageKey, DocumentPageContent>>>
> = {
    en: {
        privacy: {
            kind: 'document',
            eyebrow: 'LEGAL · PRIVACY',
            title: 'Privacy Policy',
            description:
                'How Face Lab handles the media you analyze, what stays on your device, and which third-party services this website relies on.',
            updatedAt,
            seoTitle: 'Privacy Policy — Face Lab',
            seoDescription:
                'Face Lab analyzes faces entirely in your browser. Read what stays on your device and which services this website uses.',
            sections: [
                {
                    title: 'Who operates this website',
                    paragraphs: [
                        'Face Lab (face.sunuyoruz.biz) is operated by sunuyoruz.biz. Questions about this policy can be directed through sunuyoruz.biz.',
                        'Face Lab has no accounts, profiles, or visitor database. The tool does not ask for your name, e-mail address, or any other identifying detail.',
                    ],
                },
                {
                    title: 'Your media stays on your device',
                    paragraphs: [
                        'Files you choose are opened with a temporary in-browser reference and are never uploaded. Face detection, landmarks, descriptors, expression, age, and gender estimates, and reference matching all run inside your browser with face-api.js and TensorFlow.js.',
                        'No file, video frame, face descriptor, reference label, or result is transmitted to sunuyoruz.biz or to any third party. Results live only in the memory of the open tab and are discarded when you remove the media, reset the tool, or close the page.',
                    ],
                },
                {
                    title: 'Remote media URLs',
                    paragraphs: [
                        'When you load media from a URL, your browser requests that media directly from the server you named, exactly as it would when visiting the address. That server receives your request, including your IP address, under its own privacy terms. Face Lab does not proxy, cache, or store remote media.',
                    ],
                },
                {
                    title: 'Model files',
                    paragraphs: [
                        'The neural network weights are static files served from this website. Downloading them is an ordinary web request handled by our hosting provider. If you point the model root URL setting at another server, that request goes to the server you chose.',
                    ],
                },
                {
                    title: 'Hosting',
                    paragraphs: [
                        'This website is delivered by Cloudflare Pages. To serve the pages and protect them from abuse, Cloudflare processes connection data such as your IP address and request headers under its own privacy policy. sunuyoruz.biz does not receive per-visitor logs from the tool itself.',
                    ],
                },
                {
                    title: 'Analytics',
                    paragraphs: [
                        'This website may use Google Analytics 4. When it is enabled, Google sets analytics cookies and receives page views together with a small set of tool events. IP anonymization is requested. The events carry only the following details:',
                    ],
                    items: [
                        'the selected face detection model,',
                        'whether matching, expression analysis, and age and gender estimation were switched on,',
                        'how many reference and search area faces were found,',
                        'how long the analysis took and whether it failed.',
                    ],
                },
                {
                    title: 'Advertising',
                    paragraphs: [
                        'This website may display advertisements served by Google AdSense. Google and its partners may use cookies and similar identifiers to show and measure ads, including personalized ads where your settings and region allow it. You can manage ad personalization in your Google Ads Settings and block third-party cookies in your browser. The ads.txt file published on this website lists the sellers authorized to sell its ad inventory.',
                    ],
                },
                {
                    title: 'Cookies and local storage',
                    paragraphs: [
                        'Face Lab itself sets no cookies. It stores only your color theme preference in the browser under the local storage key face_lab_theme, and it reads the interface language from the address bar. Analytics and advertising cookies are set only by Google as described above, and only when those services are enabled.',
                    ],
                },
                {
                    title: 'Browser assistant integration',
                    paragraphs: [
                        'If your browser exposes a model-context interface for on-device assistants, Face Lab registers a tool that lets such an assistant prefill media URLs and the detector choice in the visible interface. This tool does not start the analysis and does not send anything anywhere; it works within the same page.',
                    ],
                },
                {
                    title: 'Your rights',
                    paragraphs: [
                        'Because the tool keeps nothing about you, there is no Face Lab record to access, correct, or erase. Data processed by Cloudflare, Google Analytics, or Google AdSense is subject to those providers’ policies and controls. Under laws such as the GDPR and the Turkish KVKK you may have rights of access, rectification, erasure, and objection; you can exercise them with the provider concerned or reach us through sunuyoruz.biz.',
                    ],
                },
                {
                    title: 'Changes to this policy',
                    paragraphs: [
                        'We may update this policy when the website or the services it relies on change. The revision date above shows the latest version.',
                    ],
                },
            ],
        },
        terms: {
            kind: 'document',
            eyebrow: 'LEGAL · TERMS',
            title: 'Terms of Use',
            description: 'The conditions under which you may use Face Lab.',
            updatedAt,
            seoTitle: 'Terms of Use — Face Lab',
            seoDescription:
                'The conditions for using Face Lab, the browser-only face analysis tool by sunuyoruz.biz.',
            sections: [
                {
                    title: 'Acceptance',
                    paragraphs: [
                        'By using face.sunuyoruz.biz you agree to these terms and to the Privacy Policy. If you do not agree, please do not use the website.',
                    ],
                },
                {
                    title: 'What Face Lab is',
                    paragraphs: [
                        'Face Lab is a free, experimental face analysis tool that runs entirely in your browser on top of the open-source face-api.js models. It produces statistical estimates for face detection, landmarks, matching, expressions, age, and gender. It is not an identity verification, authentication, or surveillance service.',
                    ],
                },
                {
                    title: 'Responsible use and consent',
                    paragraphs: [
                        'You are solely responsible for the media you analyze and for how you use the results. Facial images and face descriptors are personal and, in many jurisdictions, biometric data protected by laws such as the GDPR and the Turkish KVKK. Use the tool only with media you have the right to use and, where required, obtain the consent of the people shown.',
                        'You must not use Face Lab to monitor, track, profile, or discriminate against people, or in any other way that is unlawful or violates the rights of others.',
                    ],
                },
                {
                    title: 'No warranty',
                    paragraphs: [
                        'All outputs are probabilistic model predictions. They can be wrong, incomplete, or biased, and they vary with lighting, pose, image quality, and settings. Outputs must not be treated as facts about a person and must never be the sole basis for a decision that has legal or similarly significant effects on someone.',
                        'The website is provided “as is” and “as available”, without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, accuracy, and non-infringement.',
                    ],
                },
                {
                    title: 'Limitation of liability',
                    paragraphs: [
                        'To the fullest extent permitted by law, sunuyoruz.biz is not liable for any direct, indirect, incidental, consequential, or special damages arising from the use of, or inability to use, this website or its outputs.',
                    ],
                },
                {
                    title: 'Third-party services and content',
                    paragraphs: [
                        'Media loaded from remote URLs belongs to its respective owners and is fetched by your browser under the terms of the hosting server. Cloudflare, Google Analytics, and Google AdSense operate under their own terms and privacy policies, which are described in the Privacy Policy.',
                    ],
                },
                {
                    title: 'Open source',
                    paragraphs: [
                        'The Face Lab source code is released into the public domain under the Unlicense. The face-api.js library and its model weights are distributed under their own open-source licenses. These terms govern the use of the hosted website, not the source code.',
                    ],
                },
                {
                    title: 'Changes and availability',
                    paragraphs: [
                        'We may change, suspend, or discontinue the website or any part of it at any time without notice, and we may revise these terms. The revision date above shows the latest version. Continued use after a change means you accept the revised terms.',
                    ],
                },
                {
                    title: 'Contact',
                    paragraphs: [
                        'Questions about these terms can be directed through sunuyoruz.biz.',
                    ],
                },
            ],
        },
    },
    tr: {
        privacy: {
            kind: 'document',
            eyebrow: 'YASAL · GİZLİLİK',
            title: 'Gizlilik Politikası',
            description:
                'Face Lab’in analiz ettiğiniz medyayı nasıl ele aldığı, cihazınızda neyin kaldığı ve bu web sitesinin hangi üçüncü taraf hizmetlere dayandığı.',
            updatedAt,
            seoTitle: 'Gizlilik Politikası — Face Lab',
            seoDescription:
                'Face Lab yüzleri tamamen tarayıcınızda analiz eder. Cihazınızda neyin kaldığını ve bu web sitesinin hangi hizmetleri kullandığını okuyun.',
            sections: [
                {
                    title: 'Bu web sitesini kim işletiyor',
                    paragraphs: [
                        'Face Lab (face.sunuyoruz.biz) sunuyoruz.biz tarafından işletilir. Bu politikayla ilgili sorularınızı sunuyoruz.biz üzerinden iletebilirsiniz.',
                        'Face Lab’de hesap, profil veya ziyaretçi veritabanı yoktur. Araç adınızı, e-posta adresinizi veya sizi tanımlayan başka bir bilgiyi istemez.',
                    ],
                },
                {
                    title: 'Medyanız cihazınızda kalır',
                    paragraphs: [
                        'Seçtiğiniz dosyalar geçici bir tarayıcı içi referansla açılır ve hiçbir zaman yüklenmez. Yüz tespiti, yüz işaretleri, betimleyiciler, ifade, yaş ve cinsiyet tahminleri ile referans eşleştirmesi face-api.js ve TensorFlow.js ile tamamen tarayıcınızın içinde çalışır.',
                        'Hiçbir dosya, video karesi, yüz betimleyicisi, referans etiketi veya sonuç sunuyoruz.biz’e ya da herhangi bir üçüncü tarafa iletilmez. Sonuçlar yalnızca açık sekmenin belleğinde yaşar; medyayı kaldırdığınızda, aracı sıfırladığınızda veya sayfayı kapattığınızda silinir.',
                    ],
                },
                {
                    title: 'Uzak medya URL’leri',
                    paragraphs: [
                        'Bir URL’den medya yüklediğinizde tarayıcınız o medyayı, adresi ziyaret ediyormuş gibi doğrudan belirttiğiniz sunucudan ister. O sunucu, IP adresiniz dâhil isteğinizi kendi gizlilik koşulları çerçevesinde alır. Face Lab uzak medyayı vekil sunucudan geçirmez, önbelleğe almaz ve saklamaz.',
                    ],
                },
                {
                    title: 'Model dosyaları',
                    paragraphs: [
                        'Sinir ağı ağırlıkları bu web sitesinden sunulan statik dosyalardır. Bunların indirilmesi, barındırma sağlayıcımız tarafından karşılanan sıradan bir web isteğidir. Model kök URL’si ayarını başka bir sunucuya yönlendirirseniz bu istek seçtiğiniz sunucuya gider.',
                    ],
                },
                {
                    title: 'Barındırma',
                    paragraphs: [
                        'Bu web sitesi Cloudflare Pages ile sunulur. Sayfaları sunmak ve kötüye kullanımdan korumak için Cloudflare, IP adresiniz ve istek başlıkları gibi bağlantı verilerini kendi gizlilik politikası çerçevesinde işler. sunuyoruz.biz aracın kendisinden ziyaretçi bazlı günlük almaz.',
                    ],
                },
                {
                    title: 'Analitik',
                    paragraphs: [
                        'Bu web sitesi Google Analytics 4 kullanabilir. Etkin olduğunda Google analitik çerezleri yerleştirir ve sayfa görüntülemelerini az sayıda araç olayıyla birlikte alır. IP anonimleştirmesi talep edilir. Olaylar yalnızca şu bilgileri taşır:',
                    ],
                    items: [
                        'seçilen yüz tespit modeli,',
                        'eşleştirme, ifade analizi ile yaş ve cinsiyet tahmininin açık olup olmadığı,',
                        'kaç referans ve arama alanı yüzü bulunduğu,',
                        'analizin ne kadar sürdüğü ve başarısız olup olmadığı.',
                    ],
                },
                {
                    title: 'Reklam',
                    paragraphs: [
                        'Bu web sitesi Google AdSense tarafından sunulan reklamlar gösterebilir. Google ve iş ortakları, ayarlarınızın ve bölgenizin izin verdiği ölçüde kişiselleştirilmiş reklamlar dâhil olmak üzere reklam göstermek ve ölçmek için çerezler ve benzeri tanımlayıcılar kullanabilir. Reklam kişiselleştirmesini Google Reklam Ayarları’ndan yönetebilir, üçüncü taraf çerezlerini tarayıcınızdan engelleyebilirsiniz. Bu web sitesinde yayımlanan ads.txt dosyası, reklam envanterini satmaya yetkili satıcıları listeler.',
                    ],
                },
                {
                    title: 'Çerezler ve yerel depolama',
                    paragraphs: [
                        'Face Lab’in kendisi çerez yerleştirmez. Yalnızca renk teması tercihinizi tarayıcıda face_lab_theme yerel depolama anahtarı altında saklar ve arayüz dilini adres çubuğundan okur. Analitik ve reklam çerezleri yalnızca yukarıda açıklandığı şekilde ve yalnızca bu hizmetler etkinken Google tarafından yerleştirilir.',
                    ],
                },
                {
                    title: 'Tarayıcı asistanı entegrasyonu',
                    paragraphs: [
                        'Tarayıcınız cihaz içi asistanlar için bir model bağlamı arayüzü sunuyorsa Face Lab, böyle bir asistanın görünür arayüzde medya URL’lerini ve dedektör seçimini önceden doldurmasına izin veren bir araç kaydeder. Bu araç analizi başlatmaz ve hiçbir yere veri göndermez; aynı sayfanın içinde çalışır.',
                    ],
                },
                {
                    title: 'Haklarınız',
                    paragraphs: [
                        'Araç sizinle ilgili hiçbir şey saklamadığından erişilecek, düzeltilecek veya silinecek bir Face Lab kaydı yoktur. Cloudflare, Google Analytics veya Google AdSense tarafından işlenen veriler bu sağlayıcıların politikalarına ve denetimlerine tabidir. KVKK ve GDPR gibi mevzuat kapsamında erişim, düzeltme, silme ve itiraz haklarınız olabilir; bu hakları ilgili sağlayıcı nezdinde kullanabilir veya bize sunuyoruz.biz üzerinden ulaşabilirsiniz.',
                    ],
                },
                {
                    title: 'Bu politikadaki değişiklikler',
                    paragraphs: [
                        'Web sitesi veya dayandığı hizmetler değiştiğinde bu politikayı güncelleyebiliriz. Yukarıdaki güncelleme tarihi en son sürümü gösterir.',
                    ],
                },
            ],
        },
        terms: {
            kind: 'document',
            eyebrow: 'YASAL · ŞARTLAR',
            title: 'Kullanım Şartları',
            description: 'Face Lab’i hangi koşullarda kullanabileceğiniz.',
            updatedAt,
            seoTitle: 'Kullanım Şartları — Face Lab',
            seoDescription:
                'sunuyoruz.biz’in yalnızca tarayıcıda çalışan yüz analizi aracı Face Lab’in kullanım koşulları.',
            sections: [
                {
                    title: 'Kabul',
                    paragraphs: [
                        'face.sunuyoruz.biz’i kullanarak bu şartları ve Gizlilik Politikası’nı kabul etmiş olursunuz. Kabul etmiyorsanız lütfen web sitesini kullanmayın.',
                    ],
                },
                {
                    title: 'Face Lab nedir',
                    paragraphs: [
                        'Face Lab, açık kaynaklı face-api.js modelleri üzerinde tamamen tarayıcınızda çalışan ücretsiz ve deneysel bir yüz analizi aracıdır. Yüz tespiti, yüz işaretleri, eşleştirme, ifade, yaş ve cinsiyet için istatistiksel tahminler üretir. Bir kimlik doğrulama, yetkilendirme veya gözetim hizmeti değildir.',
                    ],
                },
                {
                    title: 'Sorumlu kullanım ve rıza',
                    paragraphs: [
                        'Analiz ettiğiniz medyadan ve sonuçları nasıl kullandığınızdan yalnızca siz sorumlusunuz. Yüz görüntüleri ve yüz betimleyicileri kişisel veridir; pek çok hukuk düzeninde KVKK ve GDPR gibi mevzuatla korunan biyometrik veri niteliğindedir. Aracı yalnızca kullanma hakkına sahip olduğunuz medyayla kullanın ve gerektiğinde görüntülenen kişilerin rızasını alın.',
                        'Face Lab’i kişileri izlemek, takip etmek, profillemek veya ayrımcılık yapmak için ya da hukuka aykırı veya başkalarının haklarını ihlal eden başka herhangi bir biçimde kullanamazsınız.',
                    ],
                },
                {
                    title: 'Garanti verilmez',
                    paragraphs: [
                        'Tüm çıktılar olasılıksal model tahminleridir. Yanlış, eksik veya yanlı olabilir; aydınlatma, poz, görüntü kalitesi ve ayarlara göre değişir. Çıktılar bir kişi hakkında olgu olarak değerlendirilmemeli ve bir kişi üzerinde hukuki ya da benzer ölçüde önemli etki doğuran bir kararın tek dayanağı olmamalıdır.',
                        'Web sitesi, ticari elverişlilik, belirli bir amaca uygunluk, doğruluk ve ihlal etmeme dâhil açık veya zımni hiçbir garanti olmaksızın “olduğu gibi” ve “mevcut olduğu ölçüde” sunulur.',
                    ],
                },
                {
                    title: 'Sorumluluğun sınırlandırılması',
                    paragraphs: [
                        'Hukukun izin verdiği azami ölçüde sunuyoruz.biz, bu web sitesinin veya çıktılarının kullanımından ya da kullanılamamasından doğan doğrudan, dolaylı, arızi, sonuçsal veya özel hiçbir zarardan sorumlu değildir.',
                    ],
                },
                {
                    title: 'Üçüncü taraf hizmetler ve içerikler',
                    paragraphs: [
                        'Uzak URL’lerden yüklenen medya ilgili sahiplerine aittir ve barındıran sunucunun koşulları çerçevesinde tarayıcınız tarafından alınır. Cloudflare, Google Analytics ve Google AdSense, Gizlilik Politikası’nda açıklanan kendi şartları ve gizlilik politikaları çerçevesinde çalışır.',
                    ],
                },
                {
                    title: 'Açık kaynak',
                    paragraphs: [
                        'Face Lab kaynak kodu Unlicense ile kamu malı olarak yayımlanmıştır. face-api.js kütüphanesi ve model ağırlıkları kendi açık kaynak lisanslarıyla dağıtılır. Bu şartlar kaynak kodu değil, barındırılan web sitesinin kullanımını düzenler.',
                    ],
                },
                {
                    title: 'Değişiklikler ve erişilebilirlik',
                    paragraphs: [
                        'Web sitesini veya herhangi bir bölümünü önceden bildirmeksizin herhangi bir zamanda değiştirebilir, askıya alabilir veya sonlandırabilir; bu şartları güncelleyebiliriz. Yukarıdaki güncelleme tarihi en son sürümü gösterir. Bir değişiklikten sonra kullanıma devam etmeniz, güncellenen şartları kabul ettiğiniz anlamına gelir.',
                    ],
                },
                {
                    title: 'İletişim',
                    paragraphs: [
                        'Bu şartlarla ilgili sorularınızı sunuyoruz.biz üzerinden iletebilirsiniz.',
                    ],
                },
            ],
        },
    },
};
